import type { AvisoDaSala, ParticipanteDaSala, SinalDaSala } from '@orbind/core'

/**
 * A malha da sala (SPEC-20260917-0047).
 *
 * Com mais de duas pessoas não existe "o outro lado": cada participante mantém
 * uma conexão com CADA um dos demais, e a mídia continua indo direto de
 * navegador a navegador — sem servidor de mídia no meio. Para o punhado de
 * pessoas que uma sala de trabalho tem, é o desenho mais simples que funciona;
 * o preço é a banda de subida, que cresce com o número de participantes.
 *
 * Quem OFERTA é sempre quem acabou de chegar. Essa assimetria vem do servidor
 * (só quem entra recebe a lista de quem já estava) e é o que evita os dois
 * lados ofertarem ao mesmo tempo.
 *
 * Cada conexão carrega os mesmos três canais da chamada de duas pessoas: voz,
 * câmera e tela — nessa ordem. Ligar a câmera ou compartilhar a tela depois é
 * trocar o que vai no canal, nunca renegociar.
 */

const SERVIDORES_ICE: RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
]

/** De onde sai o que EU envio. Funções, porque as faixas trocam o tempo todo. */
export type FontesDeMidia = {
  audio: () => MediaStreamTrack | null
  video: () => MediaStreamTrack | null
  tela: () => MediaStreamTrack | null
}

export type ParticipanteAoVivo = ParticipanteDaSala & {
  /** Voz + câmera dele. */
  camera: MediaStream | null
  /** A tela que ele compartilha, em canal próprio. */
  tela: MediaStream | null
  /** O que ele diz que está mandando — serve para a moldura na tela. */
  midia: { video: boolean; tela: boolean }
  conectado: boolean
}

type Conexao = {
  pc: RTCPeerConnection
  participante: ParticipanteAoVivo
  /** ICE que chegou antes da descrição remota — aplicado assim que der. */
  pendentes: RTCIceCandidateInit[]
  fluxoCamera: MediaStream
  fluxoTela: MediaStream
}

export class MalhaDaSala {
  private conexoes = new Map<string, Conexao>()
  private eu: ParticipanteDaSala | null = null

  constructor(
    private readonly opcoes: {
      enviar: (para: string, sinal: SinalDaSala) => void
      aoMudar: (participantes: ParticipanteAoVivo[]) => void
      fontes: FontesDeMidia
      /**
       * Abrir conexão com TODOS da sala, membros inclusive. É o caso das salas
       * do Escritório (SPEC-20261009-2000), onde não existe chamada direta
       * entre os membros por baixo.
       */
      conectarTodos?: boolean
    },
  ) {}

  /** Quem eu sou dentro desta sala (definido quando o servidor responde). */
  get meuId(): string | null {
    return this.eu?.id ?? null
  }

  get participantes(): ParticipanteAoVivo[] {
    return [...this.conexoes.values()].map((c) => c.participante)
  }

  async tratarEvento(evento: AvisoDaSala['evento']): Promise<void> {
    switch (evento.nome) {
      case 'entrei': {
        this.eu = evento.eu
        // Eu cheguei: eu oferto para cada um que já estava.
        for (const participante of evento.participantes) {
          await this.conectar(participante, true)
        }
        this.avisar()
        return
      }
      case 'entrou': {
        // Alguém chegou: ele oferta, eu só preparo o lugar.
        await this.conectar(evento.participante, false)
        this.avisar()
        return
      }
      case 'saiu': {
        const conexao = this.conexoes.get(evento.participanteId)
        if (!conexao) return
        conexao.pc.close()
        this.conexoes.delete(evento.participanteId)
        this.avisar()
        return
      }
      case 'sinal': {
        await this.tratarSinal(evento.de, evento.sinal)
        return
      }
      default:
        return
    }
  }

  /** Coloca as faixas atuais em TODAS as conexões, sem renegociar nenhuma. */
  async atualizarMidia(): Promise<void> {
    for (const conexao of this.conexoes.values()) await this.encherCanais(conexao.pc)
  }

  /** Conta a todos o que estou mandando (câmera e/ou tela). */
  anunciarMidia(video: boolean, tela: boolean): void {
    for (const id of this.conexoes.keys()) this.opcoes.enviar(id, { acao: 'midia', video, tela })
  }

  encerrar(): void {
    for (const conexao of this.conexoes.values()) conexao.pc.close()
    this.conexoes.clear()
    this.eu = null
    this.avisar()
  }

  /* ------------------------------------------------- por dentro --- */

  private avisar(): void {
    this.opcoes.aoMudar(this.participantes)
  }

  /**
   * Com quem eu abro conexão nesta sala.
   *
   * Dois MEMBROS já se falam pela chamada direta que fez o telefone tocar —
   * abrir uma segunda conexão entre eles duplicaria voz e imagem. O que a malha
   * resolve é o convidado: quem veio pelo link fala com todo mundo, e todo
   * mundo fala com ele.
   */
  private devoConectar(outro: ParticipanteDaSala): boolean {
    if (this.opcoes.conectarTodos) return true
    return this.eu?.convidado === true || outro.convidado
  }

  private async conectar(participante: ParticipanteDaSala, euOferto: boolean): Promise<void> {
    if (this.conexoes.has(participante.id) || !this.devoConectar(participante)) return
    const pc = new RTCPeerConnection({ iceServers: SERVIDORES_ICE })
    const conexao: Conexao = {
      pc,
      participante: { ...participante, camera: null, tela: null, midia: { video: false, tela: false }, conectado: false },
      pendentes: [],
      fluxoCamera: new MediaStream(),
      fluxoTela: new MediaStream(),
    }
    this.conexoes.set(participante.id, conexao)

    pc.onicecandidate = (ev) => {
      if (!ev.candidate) return
      this.opcoes.enviar(participante.id, {
        acao: 'ice',
        candidato: {
          candidate: ev.candidate.candidate,
          sdpMid: ev.candidate.sdpMid,
          sdpMLineIndex: ev.candidate.sdpMLineIndex,
        },
      })
    }

    // A POSIÇÃO do canal de vídeo diz o que ele é: o segundo é sempre a tela.
    pc.ontrack = (ev) => {
      const videos = pc.getTransceivers().filter((t) => t.receiver.track?.kind === 'video')
      const ehTela = ev.track.kind === 'video' && videos.indexOf(ev.transceiver) === 1
      const alvo = ehTela ? conexao.fluxoTela : conexao.fluxoCamera
      alvo.addTrack(ev.track)
      conexao.participante = {
        ...conexao.participante,
        camera: new MediaStream(conexao.fluxoCamera.getTracks()),
        tela: new MediaStream(conexao.fluxoTela.getTracks()),
      }
      this.avisar()
    }

    pc.onconnectionstatechange = () => {
      const vivo = pc.connectionState === 'connected'
      if (conexao.participante.conectado === vivo) return
      conexao.participante = { ...conexao.participante, conectado: vivo }
      this.avisar()
    }

    if (euOferto) {
      pc.addTransceiver('audio', { direction: 'sendrecv' })
      pc.addTransceiver('video', { direction: 'sendrecv' })
      pc.addTransceiver('video', { direction: 'sendrecv' })
      await this.encherCanais(pc)
      const oferta = await pc.createOffer()
      await pc.setLocalDescription(oferta)
      this.opcoes.enviar(participante.id, { acao: 'oferta', sdp: oferta.sdp ?? '' })
    }
  }

  /** Voz no canal de áudio; câmera no primeiro vídeo, tela no segundo. */
  private async encherCanais(pc: RTCPeerConnection): Promise<void> {
    for (const t of pc.getTransceivers()) {
      if (t.receiver.track?.kind !== 'audio') continue
      t.direction = 'sendrecv'
      await t.sender.replaceTrack(this.opcoes.fontes.audio()).catch(() => undefined)
    }
    const videos = pc.getTransceivers().filter((t) => t.receiver.track?.kind === 'video')
    const faixas = [this.opcoes.fontes.video(), this.opcoes.fontes.tela()]
    for (const [i, t] of videos.entries()) {
      t.direction = 'sendrecv'
      await t.sender.replaceTrack(faixas[i] ?? null).catch(() => undefined)
    }
  }

  private async tratarSinal(de: string, sinal: SinalDaSala): Promise<void> {
    const conexao = this.conexoes.get(de)
    if (!conexao) return
    const { pc } = conexao

    switch (sinal.acao) {
      case 'oferta': {
        await pc.setRemoteDescription({ type: 'offer', sdp: sinal.sdp })
        // Os canais vieram na oferta: agora é só preencher com o que eu mando.
        await this.encherCanais(pc)
        await this.aplicarPendentes(conexao)
        const resposta = await pc.createAnswer()
        await pc.setLocalDescription(resposta)
        this.opcoes.enviar(de, { acao: 'resposta', sdp: resposta.sdp ?? '' })
        return
      }
      case 'resposta': {
        await pc.setRemoteDescription({ type: 'answer', sdp: sinal.sdp })
        await this.aplicarPendentes(conexao)
        return
      }
      case 'ice': {
        if (pc.remoteDescription) await pc.addIceCandidate(sinal.candidato).catch(() => undefined)
        else conexao.pendentes.push(sinal.candidato)
        return
      }
      case 'midia': {
        conexao.participante = { ...conexao.participante, midia: { video: sinal.video, tela: sinal.tela } }
        this.avisar()
        return
      }
    }
  }

  private async aplicarPendentes(conexao: Conexao): Promise<void> {
    for (const candidato of conexao.pendentes.splice(0)) {
      await conexao.pc.addIceCandidate(candidato).catch(() => undefined)
    }
  }
}
